import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';
import { Box, InputAdornment, TextField } from '@mui/material';

import { useDebounce } from 'src/hooks';
import type { ActionItemStatus } from 'src/types/followUp';
import { apiPaths } from 'src/services/api/apiPaths';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import OpenPopupProvider from 'src/components/app/context/OpenPopupContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import { ORG_UNIT_FILTER_NAME } from 'src/components/common/OrgTree/types';
import FilterSection from 'src/components/followUp/FilterColumn/FilterSection';
import FollowUpOwnerSearch from 'src/components/followUp/FilterColumn/FollowUpOwnerSearch';
import useKPIFilterGroupQuery from 'src/components/myFollowUp/hooks/useKPIFilterGroupQuery';
import useOwnerListQuery from 'src/components/myFollowUp/hooks/useOwnerListQuery';

import { styles } from './FilterColumn.styles';

export const All = 'All';
// empty default array as reference for hooks to prevent rerendering
const DEFAULT_FILTERS: never[] = [];

const actionStatus: Array<{
  value: ActionItemStatus | typeof All;
  translationKey: string;
}> = [
  { value: 'Open', translationKey: 'status.open' },
  { value: 'Closed', translationKey: 'status.closed' },
  { value: 'In Progress', translationKey: 'status.inProgress' },
  { value: 'Overdue', translationKey: 'status.overdue' },
];

const actionHasPriorityOrgUnit: Array<{
  value: string;
  translationKey: string;
}> = [
  { value: '1', translationKey: 'hasPriorityOrgUnit.only' },
  { value: '0', translationKey: 'hasPriorityOrgUnit.none' },
];

export interface FilterSections {}

const FilterColumn: FC<FilterSections> = () => {
  const { t } = useTranslation(['followUpActions', 'common']);
  const { user, currentSurvey } = useUserContext();
  const { filterGroups, setFilter } = useFilterContext();

  const orgUnitIdsKey = useMemo(
    () =>
      filterGroups[ORG_UNIT_FILTER_NAME]?.map(
        (orgUnitFilterObj) => orgUnitFilterObj.filterExpr.orgUnit,
      ).join(',') ?? '',
    [filterGroups],
  );
  const orgUnitIds = useMemo(
    () => (orgUnitIdsKey ? orgUnitIdsKey.split(',') : undefined),
    [orgUnitIdsKey],
  );

  // Search
  const [searchString, setSearchString] = useState<string>('');
  const debouncedSearchString = useDebounce(searchString, 500);
  useEffect(() => {
    setFilter(
      'search',
      debouncedSearchString
        ? [
            {
              label: debouncedSearchString,
              filterExpr: { value: debouncedSearchString },
            },
          ]
        : [],
    );
  }, [debouncedSearchString]);

  // Follow-Up Owner filter
  const [ownerSearchString, setOwnerSearchString] = useState<string>('');
  const debouncedOwnerSearch = useDebounce(ownerSearchString, 500);
  const { data: followUpOwners } = useOwnerListQuery({
    path: apiPaths.FOLLOW_UP_ACTIONS.OWNERS,
    search: debouncedOwnerSearch,
  });
  const ownerFilterOptions = useMemo(
    () => [
      {
        value: user ? String(user.id) : '',
        displayString: t('filter.myActions'),
      },
      ...(followUpOwners?.map(({ id, fullName }) => ({
        value: String(id),
        displayString: fullName,
      })) ?? DEFAULT_FILTERS),
    ],
    [user, t, followUpOwners],
  );

  // KPI Filter
  const { data: kpiFilterGroup } = useKPIFilterGroupQuery(
    currentSurvey?.id,
    orgUnitIds,
  );
  const kpis = useMemo(
    () =>
      kpiFilterGroup?.filters?.map((filter) => ({
        id: filter.id,
        short: filter.shortcut,
        label: filter.label,
      })) || DEFAULT_FILTERS,
    [kpiFilterGroup],
  );
  const kpiFilterOptions = useMemo(
    () =>
      kpis?.map(({ id, short, label }) => ({
        value: String(id),
        displayString: `${label} (${short})`,
      })) ?? DEFAULT_FILTERS,
    [kpis],
  );

  // Status filter
  const statusFilterOptions = useMemo(
    () =>
      actionStatus.map(({ value, translationKey }) => ({
        value,
        displayString: t(translationKey),
      })),
    [actionStatus, t],
  );

  // Has priority org unit filter
  const hasPriorityOrgUnitOptions = useMemo(
    () =>
      actionHasPriorityOrgUnit.map(({ value, translationKey }) => ({
        value,
        displayString: t(translationKey),
      })),
    [actionHasPriorityOrgUnit, t],
  );

  return (
    <OpenPopupProvider>
      <Box sx={styles.filterColumn}>
        <TextField
          aria-label="FollowUp Action search"
          fullWidth
          placeholder={t('filter.search')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaSearch />
              </InputAdornment>
            ),
            sx: (theme) => ({
              border: '2px solid',
              borderColor: theme.colors.neutral[100],
            }),
          }}
          value={searchString}
          onChange={(event) => setSearchString(event.target.value)}
        />
        <FilterSection
          filterKey={'followUpOwner'}
          allFilterOptions={ownerFilterOptions}
          popupKey="owner"
          searchBar={
            <FollowUpOwnerSearch
              searchString={ownerSearchString}
              setSearchString={setOwnerSearchString}
            />
          }
        >
          {t('filter.followUpOwnerFilterLabel')}
        </FilterSection>

        <FilterSection
          filterKey={'kpi'}
          allFilterOptions={kpiFilterOptions}
          popupKey="kpi"
        >
          {t('filter.kpiFilterLabel')}
        </FilterSection>

        <FilterSection
          filterKey={'status'}
          allFilterOptions={statusFilterOptions}
          popupKey="status"
        >
          {t('filter.statusFilterLabel')}
        </FilterSection>

        <FilterSection
          filterKey={'hasPriorityOrgUnit'}
          allFilterOptions={hasPriorityOrgUnitOptions}
          popupKey="hasPriorityOrgUnit"
        >
          {t('filter.priorityOrgUnitFilterLabel')}
        </FilterSection>
      </Box>
    </OpenPopupProvider>
  );
};

export default FilterColumn;
